export interface Deliverable {
  id: string;
  name: string;
  description: string;
  frequency: string;
  included: boolean;
}

export interface Pack {
  id: string;
  name: string;
  description: string;
  price: number;
  billingFrequency: 'monthly' | 'quarterly' | 'yearly';
  commitment: number; // en mois
  features: string[];
  deliverables: Deliverable[];
  highlights: string[];
  recommended?: boolean;
}

export interface ProjectPhase {
  id: string;
  name: string;
  duration: number; // en jours
  startOffset: number; // en jours depuis le début
  tasks: {
    name: string;
    duration: number;
    dependencies?: string[];
  }[];
  deliverables: string[];
}

export interface Proposal {
  id: string;
  restaurantId: string;
  date: string;
  validUntil: string;
  packs: Pack[];
  projectPhases: ProjectPhase[];
  customization?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
}

// Données des livrables
export const deliverables: Record<string, Deliverable> = {
  audit: {
    id: 'audit',
    name: 'Audit SEO & GEO complet',
    description: 'Analyse technique, sémantique et locale avec recommandations',
    frequency: 'Trimestriel',
    included: true
  },
  keywords: {
    id: 'keywords',
    name: 'Recherche de mots-clés',
    description: 'Identification des opportunités EN/ES/PT',
    frequency: 'Mensuel',
    included: true
  },
  content: {
    id: 'content',
    name: 'Production de contenu',
    description: 'Rédaction optimisée multilingue avec brief',
    frequency: 'Mensuel',
    included: true
  },
  backlinks: {
    id: 'backlinks',
    name: 'Acquisition de backlinks',
    description: 'Prospection et placement de liens qualifiés',
    frequency: 'Mensuel',
    included: true
  },
  local: {
    id: 'local',
    name: 'Optimisation locale',
    description: 'Gestion GBP, citations et avis',
    frequency: 'Hebdomadaire',
    included: true
  },
  reporting: {
    id: 'reporting',
    name: 'Reporting personnalisé',
    description: 'Suivi des KPIs et ROI',
    frequency: 'Mensuel',
    included: true
  },
  consulting: {
    id: 'consulting',
    name: 'Consulting stratégique',
    description: 'Accompagnement et recommandations',
    frequency: 'Mensuel',
    included: false
  },
  training: {
    id: 'training',
    name: 'Formation équipe',
    description: 'Sessions de formation SEO/GEO',
    frequency: 'Trimestriel',
    included: false
  }
};

// Définition des packs
export const packs: Pack[] = [
  {
    id: 'essential',
    name: 'STRATOS Essentiel',
    description: 'Fondamentaux SEO & GEO pour restaurants',
    price: 1000,
    billingFrequency: 'monthly',
    commitment: 6,
    features: [
      'Audit initial complet',
      '2 langues (FR + EN)',
      '4 contenus / mois',
      '5 backlinks / mois',
      'Optimisation GBP basique',
      'Reporting mensuel'
    ],
    deliverables: [
      deliverables.audit,
      deliverables.keywords,
      deliverables.content,
      deliverables.local,
      deliverables.reporting
    ],
    highlights: [
      'Idéal pour démarrer',
      'ROI rapide',
      'Flexibilité'
    ]
  },
  {
    id: 'premium',
    name: 'STRATOS Premium',
    description: 'Visibilité internationale optimale',
    price: 2000,
    billingFrequency: 'monthly',
    commitment: 6,
    features: [
      'Audit trimestriel',
      '3 langues (FR/EN/ES)',
      '8 contenus / mois',
      '10 backlinks / mois',
      'Chatbot IA avancé (FR/EN/ES)',
      'Entrainement FAQ & menus',
      'Optimisation GBP avancée',
      'Reporting personnalisé',
      'Consulting mensuel'
    ],
    deliverables: [
      deliverables.audit,
      deliverables.keywords,
      deliverables.content,
      deliverables.backlinks,
      deliverables.local,
      deliverables.reporting,
      deliverables.consulting
    ],
    highlights: [
      'Best-seller',
      'Couverture complète',
      'Support dédié'
    ],
    recommended: true
  },
  {
    id: 'signature',
    name: 'STRATOS Signature',
    description: 'Excellence SEO & GEO sur-mesure',
    price: 3500,
    billingFrequency: 'monthly',
    commitment: 6,
    features: [
      'Audit mensuel',
      '4 langues (FR/EN/ES/PT)',
      '12 contenus / mois',
      '15 backlinks / mois',
      'Chatbot IA Premium (4 langues)',
      'Entrainement personnalisé complet',
      'Analytics conversationnel avancé',
      'Optimisation GBP premium',
      'Reporting sur-mesure',
      'Consulting hebdomadaire',
      'Formation équipe'
    ],
    deliverables: [
      deliverables.audit,
      deliverables.keywords,
      deliverables.content,
      deliverables.backlinks,
      deliverables.local,
      deliverables.reporting,
      deliverables.consulting,
      deliverables.training
    ],
    highlights: [
      'Service VIP',
      'Résultats garantis',
      'Accompagnement complet'
    ]
  }
];

// Planning projet type
export const projectPhases: ProjectPhase[] = [
  {
    id: 'phase1',
    name: 'Phase 1 : Audit & Stratégie',
    duration: 15,
    startOffset: 0,
    tasks: [
      {
        name: 'Audit technique complet',
        duration: 5
      },
      {
        name: 'Analyse sémantique',
        duration: 5
      },
      {
        name: 'Audit local GEO',
        duration: 5
      }
    ],
    deliverables: [
      'Rapport d\'audit complet',
      'Recommandations prioritaires',
      'Planning d\'actions'
    ]
  },
  {
    id: 'phase2',
    name: 'Phase 2 : Optimisation Technique',
    duration: 30,
    startOffset: 15,
    tasks: [
      {
        name: 'Corrections techniques',
        duration: 10,
        dependencies: ['phase1']
      },
      {
        name: 'Optimisation mobile',
        duration: 10
      },
      {
        name: 'Configuration multilingue',
        duration: 10
      }
    ],
    deliverables: [
      'Rapport d\'optimisation',
      'Tests de performance',
      'Documentation technique'
    ]
  },
  {
    id: 'phase3',
    name: 'Phase 3 : Contenu & Liens',
    duration: 45,
    startOffset: 45,
    tasks: [
      {
        name: 'Production de contenu',
        duration: 30,
        dependencies: ['phase2']
      },
      {
        name: 'Acquisition de backlinks',
        duration: 30
      },
      {
        name: 'Optimisation GBP',
        duration: 15
      }
    ],
    deliverables: [
      'Contenus optimisés',
      'Rapport de backlinks',
      'Bilan GBP'
    ]
  }
];

// Proposition type
export const proposalTemplate: Proposal = {
  id: 'proposal_default',
  restaurantId: '39v',
  date: new Date().toISOString(),
  validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // +30 jours
  packs,
  projectPhases,
  customization: {
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af'
  }
};

export const getProposalForRestaurant = (restaurantId: string): Proposal => {
  return {
    ...proposalTemplate,
    restaurantId,
    id: `proposal_${restaurantId}`,
    date: new Date().toISOString()
  };
};
